import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const STORAGE_KEY = 'h_cart_v1';
const DRAWER_EVENT = 'h-cart-open';

const CartCtx = createContext(null);

function loadCart() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((l) => l && l.sku && l.slug && typeof l.qty === 'number');
  } catch {
    return [];
  }
}

function saveCart(lines) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch { /* ignore */ }
}

export function CartProvider({ children }) {
  const [lines, setLines] = useState(() => loadCart());
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => { saveCart(lines); }, [lines]);

  // Cross-component open drawer via custom event
  useEffect(() => {
    const handler = () => setDrawerOpen(true);
    window.addEventListener(DRAWER_EVENT, handler);
    return () => window.removeEventListener(DRAWER_EVENT, handler);
  }, []);

  const addItem = useCallback((line) => {
    setLines((prev) => {
      const next = [...prev];
      const idx = next.findIndex((l) => l.sku === line.sku);
      if (idx >= 0) {
        next[idx] = { ...next[idx], qty: Math.min(20, (next[idx].qty || 0) + (line.qty || 1)) };
      } else {
        next.push({ ...line, qty: Math.min(20, line.qty || 1) });
      }
      return next;
    });
    setDrawerOpen(true);
  }, []);

  const updateQty = useCallback((sku, qty) => {
    setLines((prev) => prev.map((l) => (l.sku === sku ? { ...l, qty: Math.max(1, Math.min(20, qty)) } : l)));
  }, []);

  const removeItem = useCallback((sku) => {
    setLines((prev) => prev.filter((l) => l.sku !== sku));
  }, []);

  const clearCart = useCallback(() => setLines([]), []);

  const openDrawer = useCallback(() => setDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);

  const totals = useMemo(() => {
    const itemCount = lines.reduce((n, l) => n + (l.qty || 0), 0);
    const subtotal = lines.reduce((s, l) => s + (l.unit_price || 0) * (l.qty || 0), 0);
    const shipping = subtotal === 0 ? 0 : subtotal >= 200 ? 0 : 10;
    const total = subtotal + shipping;
    return { itemCount, subtotal, shipping, total };
  }, [lines]);

  const value = useMemo(() => ({
    lines, totals, drawerOpen,
    addItem, updateQty, removeItem, clearCart,
    openDrawer, closeDrawer,
  }), [lines, totals, drawerOpen, addItem, updateQty, removeItem, clearCart, openDrawer, closeDrawer]);

  return <CartCtx.Provider value={value}>{children}</CartCtx.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartCtx);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};

export function openCartDrawer() {
  window.dispatchEvent(new Event(DRAWER_EVENT));
}
